import { ApolloQueryResult, useQuery } from '@apollo/client';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import SectionMessage from '@atlaskit/section-message';
import {
  analytics,
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  PlanType,
  sendAnalyticsEvent,
} from '@atlassian/temp-analytics';
import {
  BillEstimateOrderItemDto,
  Currency,
  GetOrderPreviewDto,
  OfferingStatus,
  PricingType,
} from '@atlassian/cssxp-ccp-types';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  EntitlementGroup,
  FetchAvailableOfferingsForChangeSubscriptionQuery,
  FetchAvailablePricingPlansQuery,
  FetchLinkedEntitlementsForChangeSubcsriptionQuery,
  FetchLinkedEntitlementsForChangeSubcsriptionQueryVariables,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  Maybe,
  Offering,
  PaymentMethodType,
  PricingPlan,
  Promotion,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { termPaymentVerification, updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { ENABLE_REPORT_ERROR } from '@atlassian/temp-shared';
import { creditPolicyResponse, txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { EntitlementDefaultsResponse, OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  EndsAtType,
  LinkedEntitlementsInstance,
  PricingPlanOption,
  PurchaseFor,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import {
  constructCustomOrderRequest,
  EntitlementEdition,
  getCorrectedCurrentDateForAnnual,
  getDefaultPricingPlan,
  getParentContainerId,
  getPricingPlanDescription,
  getUserTierOptions,
  isFreePlanEntitlement,
  isOfferingArchived,
  isOfferingAtNotice,
  notActivePricingStatus,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ANNUAL_TERMS,
  ENABLE_CREDIT_MANAGEMENT,
  ENABLE_IMMEDIATE_DOWNGRADE_FOR_FREE_ENTITLEMENT,
  ENABLE_OMS_SERVICE_V2,
  ENABLE_PARTNER_PROMOTION,
  ENABLE_REMOVE_PROMOTION_API,
  ENABLE_TICKET_CREATION_IN_REPORTING,
} from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  EntitlementIcon,
  ErrorComponents,
  getGSACBaseURL,
  InflightInvoiceBanner,
  InfoPanel,
  InvoiceGroupSelectItem,
  isDiscretionaryDiscountPromotion,
  NotificationTypes,
  OfferingOption,
  SelectPromotionSelectItem,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  UserTierOption,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import {
  currentEntitlementType,
  getChargeElement,
  getLinkedEntitlementsDetails,
  isValidURL,
} from '@css-xp/base/entitlement-utils';
import { CustomValues } from '@css-xp/base/manage-promotions-section';
import assign from 'lodash/assign';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { getFormError, validateBillTo } from '../../../../entitlement-page/src/components/entitlement-page-utils';
import { getCustomPromotionInstance } from '../../utils/promotion-utils';
import { ManageBillingCycle, TermLengthSelectItem } from './manage-billing-cycle';
import ManageBillingProfile from './manage-billing-profile';
import {
  checkisFormDirty,
  fetchAvailableOfferings,
  fetchPricingPlans,
  getAppUserTierForBillingCycleChange,
  getAvailablePromotionsQuery,
  getChargeQuantity,
  getChargingDetails,
  getCommonEntitlementDetails,
  getDefaultAPIEntitlementData,
  getDefaultSkipTrialValues,
  getFinalLinkedEntitlements,
  getFormStateWithoutAddedPromotionFields,
  getFormStateWithoutRemovedPromotionFields,
  getFormValuesForCurrentEntitlement,
  getFormValuesForLinkedEntitlements,
  getIntendedBillingPeriodState,
  getPartnerPromotionQuery,
  getPricingEligibility,
  getPromotionsRequestBody,
  isImmediateAmendmentToFreeOrder,
  isInvalidCutShortTrial,
} from './manage-entitlement-helper';
import './manage-entitlement-v2.scss';
import { ManagePlanV2 } from './manage-plan-v2';
import { fetchLinkedEntitlementsForChangeSubcsription } from './queries/fetch-linked-entitlements-for-change-subscription';
import { ReviewChangeV2 } from './review-change';
import { buildMessage } from './utils/message-builder';

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

enum FormStages {
  BILLING_CYCLE = 'Billing cycle',
  PLAN_DETAILS = 'Plan details',
  REVIEW = 'Review',
}

enum FormStagesV2 {
  BILLING_CYCLE = 'Billing cycle',
  PLAN_DETAILS = 'Plan details',
  BILLING_PROFILE = 'Billing details',
  REVIEW = 'Review',
}

export type AvailablePromotionsType = {
  error?: any;
  loading?: {
    [key: string]: boolean;
  };
  response?: {
    [key: string]: (Promotion | null)[] | null | undefined;
  };
};

export type PartnerPromotionType = {
  error?: any;
  loading?: {
    [key: string]: boolean;
  };
  response?: {
    [key: string]: Promotion | null | undefined;
  };
};

export interface AvailableOfferings {
  response?: {
    [key: string]: Maybe<Array<Maybe<Offering>>> | undefined;
  };
  loading: boolean;
}

export interface PushPaymentCreditDetails {
  response?: {
    totalCreditLimit: number;
    availableCreditLimit: number;
  };
  loading: boolean;
}
export interface AvailablePricingPlans {
  response?: {
    [key: string]: {
      [key: string]: Maybe<Array<Maybe<PricingPlan>>> | undefined;
    };
  };
  loading: boolean;
}

export interface DefaultAPIResponse extends EntitlementDefaultsResponse {
  offeringId: string;
}

export interface DefaultApiData {
  response?: {
    [key: string]: {
      [key: string]: DefaultAPIResponse;
    };
  };
  loading: boolean;
}

export interface OrderPreviewData {
  response?: GetOrderPreviewDto;
  loading: boolean;
  error?: any;
}

export interface RefetchLoadingState {
  loading: {
    [key: string]: boolean;
  };
}

export interface TierValidationDetails {
  allowed: boolean;
  productId: EntitlementDetails['productId'] | LinkedDestinationEntitlement['productId'];
  activeUsers: EntitlementDetails['activeUsers'] | LinkedDestinationEntitlement['productId'];
}

export interface TierValidationType {
  [key: string]: TierValidationDetails;
}

export interface CoBillData {
  response?: EntitlementGroup[] | undefined | null;
  loading: boolean;
}

interface BillEstimateResponse {
  hasDiscretionaryEstimate: boolean;
  estimate: BillEstimateOrderItemDto;
  linkedEstimates: BillEstimateOrderItemDto[];
  discretionaryDiscountAmount: number;
}

export interface Estimate {
  response?: BillEstimateResponse;
  error?: any;
  loading: boolean;
}

export interface DiscretionaryEstimate {
  error?: any;
  discretionaryDiscountAmount?: number;
  loading: boolean;
  isDisabled?: boolean;
}

interface PromotionFormValues {
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: CustomValues[];
  customValue?: string[];
  promoCode?: string;
  promotionsToAdd?: SelectPromotionSelectItem[];
  promotionsToRemove?: SelectPromotionSelectItem[];
  schedulePromotionsToRemove?: SelectPromotionSelectItem[];
}

interface ManageSubscriptionPanelFormValues extends PromotionFormValues {
  offering?: OfferingOption | null;
  pricingPlan?: PricingPlanOption | null;
  userTier?: UserTierOption | null;
  units?: string;
  skipTrial?: boolean;
  skipTrialEligible?: boolean;
  skipTrialDisabled?: boolean;
  hasOptedForUpgradeTrial?: boolean;
}

interface FormDataV2 extends ManageSubscriptionPanelFormValues {
  billingFrequency: BillingFrequency;
  termLength?: TermLengthSelectItem | null;
  purchaseFor?: string | null | undefined;
  supportTicketLink?: string;
  notes?: string;
  consentTaken?: boolean;
  immediateAmendment?: boolean;
  invoiceGroupId?: InvoiceGroupSelectItem;
  isMultiUse?: boolean;
  ponumber?: string;
  coBillOption?: boolean;
  [key: string]: any;
}

export interface FormState extends FormDataV2 {
  nextBillingAnchorTimestamp?: number;
  endTimestamp?: number | null | undefined;
  coBillTimestamp?: number | null | undefined;
}

interface Props {
  entitlementDetails: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export const currentDate = moment.utc(new Date());

export const ManageEntitlementV2 = withApollo(({ entitlementDetails, transactionAccountContext, refetch }: Props) => {
  let entitlement = cloneDeep(entitlementDetails);

  if (entitlement?.upgradeTrial) {
    entitlement = {
      ...entitlement,
      offering: {
        ...entitlement.upgradeTrial.offering,
      } as Offering,
      offeringId: entitlement.upgradeTrial.offering?.id,
      edition: entitlement.upgradeTrial.offering?.name,
      pricingPlan: {
        ...entitlement.upgradeTrial.pricingPlan,
      } as PricingPlan,
    };
  }

  const [isEnabled] = useFeatureToggle();
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const isPartnerPromotionEnabled = isEnabled(ENABLE_PARTNER_PROMOTION);
  const FormTrackerStep = isAnnualTermEnabled
    ? {
        BILLING_CYCLE: 0,
        PLAN_DETAILS: 1,
        BILLING_PROFILE: 2,
        REVIEW: 3,
      }
    : {
        BILLING_CYCLE: 0,
        PLAN_DETAILS: 1,
        REVIEW: 2,
      };
  const enableImmediateDowngradeForFreeEntitlement = isEnabled(ENABLE_IMMEDIATE_DOWNGRADE_FOR_FREE_ENTITLEMENT);
  const patchRemovePromotionEnabled = isEnabled(ENABLE_REMOVE_PROMOTION_API);
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
  const gsacBaseUrl = getGSACBaseURL();
  const { data, getTransactionAccount, getCreditDetails } = transactionAccountContext;
  const transactionAccount = getTransactionAccount(data) as TransactionAccount;
  const creditDetails = getCreditDetails && getCreditDetails(data);
  const getNonTermPaymentInvoicegroups = (
    invoiceGroupsList: TransactionAccount['availableInvoiceGroups']
  ): InvoiceGroup[] => {
    return invoiceGroupsList?.nodes?.filter(
      (item) => item?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
    ) as InvoiceGroup[];
  };
  const availableInvoiceGroups =
    isCreditManagementEnabled || isStatuspage(entitlement?.productId)
      ? (defaultTo(transactionAccount?.availableInvoiceGroups?.nodes, []) as InvoiceGroup[])
      : getNonTermPaymentInvoicegroups(transactionAccount.availableInvoiceGroups);

  const [grandTotal, setGrandTotal] = React.useState<number>(0);
  const approvalEval = React.useRef<any>();
  const [currentProgressIndex, setCurrentProgressIndex] = React.useState<number>(0);
  const [customDiscount, setCustomDiscount] = React.useState<number>(0);
  const [discretionaryEstimate, setDiscretionaryEstimate] = React.useState<Partial<DiscretionaryEstimate>>({
    loading: false,
  });

  // Billing Profile with missing payment method or ship to address are invalid .
  const [isBillingProfileInValid, setIsBillingProfileInValid] = React.useState<boolean>(false);
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const { closeSidebar } = useSidebar();
  const {
    isDiscountApprovalFlowEnabled,
    isPricingPlanChangeApprovalFlowEnabled,
    isAnnualPricingPlanChangeApprovalFlowEnabled,
    isAnnualDiscountApprovalFlowEnabled,
    isSkipBulkAmendmentApprovalsEnabled,
  } = useApprovalDeskPermissions();
  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });
  const [estimatedBill, setEstimatedBill] = React.useState<Estimate>({ loading: true });
  const [termPaymentValidation, setTermPaymentValidation] = useState<{
    response?: creditPolicyResponse;
    loading: boolean;
  }>({ loading: false, response: { status: txaPushPaymentStatus.ELIGIBLE } });

  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isDiscountApprovalFlowEnabled || !isPricingPlanChangeApprovalFlowEnabled,
  });
  const parentContainerID = getParentContainerId(entitlement);
  const isStatusPageProduct = isStatuspage(entitlement?.productId);
  const isCurrentEntitlementOnTrial = isTrialEntitlement(entitlement, isStatusPageProduct);
  const isCurrentEntitlementFree = isFreePlanEntitlement(entitlement);
  const isExistingAnnualPlanForJCA = entitlement?.billingFrequency === BillingFrequency.ANNUAL && !isStatusPageProduct;
  const [{ linkedEntitlements, loadingLinkedEntitlements }, updateLinkedEntitlements] = useState<{
    linkedEntitlements: LinkedDestinationEntitlement[];
    loadingLinkedEntitlements: boolean;
  }>({ linkedEntitlements: [], loadingLinkedEntitlements: !isStatusPageProduct });
  const [isDeferredPaymentIGSelectionBlocked, setIsDeferredPaymentIGSelectionBlocked] = useState<boolean>(false); // gets set to true if deferred payment Ig is not allowed but users selects it .
  const [finalLinkedEntitlements, updateFinalLinkedEntitlements] = useState<LinkedDestinationEntitlement[]>([]);
  const [availablePromotionsData, setAvailablePromotionsData] = React.useState<AvailablePromotionsType>({});
  const [partnerPromotionData, setPartnerPromotionData] = React.useState<PartnerPromotionType>({});
  const [hasAnnualLinkedEntitlements, updateHasAnnualLinkedEntitlements] = useState<boolean>(false);
  const [refetchLoadingState, updateRefetchLoadingState] = useState<RefetchLoadingState>({
    loading: {},
  });
  const [trialValidationMsg, setTrialValidationMsg] = useState<string>('');
  const [defaultApiData, updateDefaultApiData] = useState<DefaultApiData>({
    loading: false,
  });
  const [availablePricingPlans, updateAvailablePricingPlans] = useState<AvailablePricingPlans>({
    loading: false,
  });
  const [availableOfferings, updateAvailableOfferings] = useState<AvailableOfferings>({
    loading: false,
  });
  const [orderPreviewData, setOrderPreviewdata] = React.useState<OrderPreviewData>({
    loading: false,
  });
  const [coBillData, setCoBillData] = React.useState<CoBillData>({
    loading: false,
  });
  const [isFormDirty, setIsFormDirty] = React.useState<boolean | null>(null);
  const [pristineFormState, setPristineFormState] = React.useState<FormState>();
  const [tierValidationState, setTierValidationState] = useState<TierValidationType>({});
  const enableTicketCreationOption = isEnabled(ENABLE_TICKET_CREATION_IN_REPORTING);

  let currentEntitlementEndTimestamp: number | null | undefined;
  let currentEntitlementPurchaseFor;

  if (isAnnualTermEnabled && isExistingAnnualPlanForJCA) {
    if (entitlement?.endTimestamp) {
      currentEntitlementEndTimestamp = entitlement?.endTimestamp;
      currentEntitlementPurchaseFor = PurchaseFor.ANNUAL_TERM;
    } else {
      currentEntitlementPurchaseFor = PurchaseFor.ANNUAL_AUTO_RENEW;
    }
  }

  const [formState, updateFormStateValues] = useState<FormState>({
    billingFrequency: isCurrentEntitlementFree
      ? BillingFrequency.MONTHLY
      : (entitlement.billingFrequency as BillingFrequency),
    invoiceGroupId: {
      invoiceGroup: entitlement.billingGroupId
        ? availableInvoiceGroups?.filter((invoiceGroup) => invoiceGroup.id === entitlement.billingGroupId)[0]
        : availableInvoiceGroups?.[0],
      value: (entitlement?.billingGroupId || availableInvoiceGroups?.[0]?.id) as string,
    },
    endTimestamp: currentEntitlementEndTimestamp,
    purchaseFor: currentEntitlementPurchaseFor,
    ...getFormValuesForCurrentEntitlement({
      entitlement,
      linkedEntitlements,
      isAnnualTermEnabled,
      isBillingCycleChanged: false,
    }),
  });

  // Form submit types to be added when completely shifted to oms v2 code
  const {
    error: formError,
    requestTraceId: formErrorTraceId,
    serviceName: formErrorSvcName,
    isProcessing,
    submitForm,
    complete,
    data: response,
  } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
  });
  const currentInvoiceGroup = useMemo(() => {
    return ((transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[])?.find(
      (invoiceGroup: InvoiceGroup) => {
        return invoiceGroup.id === entitlement.billingGroupId;
      }
    );
  }, [entitlement, transactionAccount]);
  const validateBillToAddress = useMemo(() => {
    return validateBillTo(transactionAccount);
  }, [transactionAccount]);
  const discreteDiscountPromotions = React.useMemo(() => {
    const promotions = [];
    const promotionKeys = Object.keys(formState).filter((key) => key.trim().includes('promotionId'));
    for (let i = 0; i < promotionKeys.length; i++) {
      if (isDiscretionaryDiscountPromotion(formState, promotionKeys[i])) {
        promotions.push(getCustomPromotionInstance(i, formState));
      }
    }
    return promotions;
  }, [formState]);

  let isBillingCycleChanged = false;
  let isAnnualBillingCycleChanged = false;
  if (isCurrentEntitlementFree) {
    if (!hasAnnualLinkedEntitlements) {
      isBillingCycleChanged = formState.billingFrequency !== BillingFrequency.MONTHLY;
    }
  } else if (!isAnnualTermEnabled && entitlement.billingFrequency === BillingFrequency.ANNUAL) {
    isAnnualBillingCycleChanged = !!formState.termLength;

    isBillingCycleChanged =
      formState.billingFrequency !== entitlement.billingFrequency ? true : isAnnualBillingCycleChanged;
  } else {
    isBillingCycleChanged = formState.billingFrequency !== entitlement.billingFrequency;
  }

  const isMonthlySelected = formState.billingFrequency === BillingFrequency.MONTHLY;
  const isAnnualSelected = formState.billingFrequency === BillingFrequency.ANNUAL;
  const isAnnualAutoRenewSelected =
    formState.billingFrequency === BillingFrequency.ANNUAL && formState.purchaseFor === PurchaseFor.ANNUAL_AUTO_RENEW;
  const isAnnualTermedSelected =
    formState.billingFrequency === BillingFrequency.ANNUAL && formState.purchaseFor === PurchaseFor.ANNUAL_TERM;
  const filteredLinkedEntitlements = useMemo(() => {
    if (formState.billingFrequency === BillingFrequency.ANNUAL && !isBillingCycleChanged) {
      return entitlement?.isApp ? [] : linkedEntitlements?.filter((item) => !!item.isApp);
    }

    return linkedEntitlements;
  }, [linkedEntitlements, formState.billingFrequency, isBillingCycleChanged]);
  const isRefetchingData = useMemo(
    () => !!Object.keys(refetchLoadingState?.loading)?.find((key) => !!refetchLoadingState?.loading[key]),
    [refetchLoadingState]
  );

  const [tierChangeAllowed, tierChangeErrorDetails] = useMemo(() => {
    let details: TierValidationDetails | undefined;
    let isAllowed = true;
    Object.keys(tierValidationState)?.forEach((key) => {
      if (!tierValidationState?.[key]?.allowed) {
        details = tierValidationState[key];
        isAllowed = false;
      }
    });

    return [isAllowed, details];
  }, [tierValidationState]);

  const noActiveOfferingForBillingCycleChange = useMemo(() => {
    if (isBillingCycleChanged && !isStatusPageProduct) {
      const noActiveOfferingForLinkedEnt = filteredLinkedEntitlements?.some(
        (item) => item.offering?.status && item.offering?.status !== OfferingStatus.ACTIVE
      );

      if (
        (entitlement.offering?.status && entitlement.offering?.status !== OfferingStatus.ACTIVE) ||
        noActiveOfferingForLinkedEnt
      ) {
        return true;
      }
    }

    return false;
  }, [isBillingCycleChanged, filteredLinkedEntitlements]);

  const isBulkAmendment = useMemo(() => {
    return (
      (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) &&
      finalLinkedEntitlements?.length > 0
    );
  }, [isBillingCycleChanged, finalLinkedEntitlements, formState.billingFrequency]);

  const skipApproval = isSkipBulkAmendmentApprovalsEnabled && isBulkAmendment;

  const onFetchLinkedEntitlementsCompleted = async (data?: FetchLinkedEntitlementsForChangeSubcsriptionQuery) => {
    const linkedEntitlementDetails = data?.entitlementDetails?.linkedEntitlements as LinkedDestinationEntitlement[];
    const activeLinkedEntitlements: LinkedDestinationEntitlement[] = [];
    let hasAnnualLinkedEnts = false;
    let endTimestamp;

    linkedEntitlementDetails?.forEach((linkedEnt) => {
      if (linkedEnt.billingFrequency === BillingFrequency.ANNUAL) {
        hasAnnualLinkedEnts = true;
        endTimestamp = linkedEnt?.endTimestamp;
      }

      if (
        !(
          (linkedEnt?.isSandbox && linkedEnt?.edition?.toUpperCase() === EntitlementEdition.FAMILY_SANDBOX) ||
          linkedEnt?.entitlementId === parentContainerID
        ) &&
        linkedEnt?.billingStatus === BillingStatus.ACTIVE
      ) {
        const linkedEntitlementObj = cloneDeep(linkedEnt);

        if (linkedEntitlementObj.upgradeTrial) {
          linkedEntitlementObj.pricingPlan = {
            ...linkedEntitlementObj.upgradeTrial.pricingPlan,
          } as PricingPlan;
        }

        activeLinkedEntitlements.push(linkedEntitlementObj);
      }
    });

    if (isCurrentEntitlementFree && hasAnnualLinkedEnts) {
      if (isAnnualTermEnabled) {
        if (endTimestamp) {
          updateFormState({
            billingFrequency: BillingFrequency.ANNUAL,
            purchaseFor: PurchaseFor.ANNUAL_TERM,
            endTimestamp,
          });
        } else {
          updateFormState({
            billingFrequency: BillingFrequency.ANNUAL,
            purchaseFor: PurchaseFor.ANNUAL_AUTO_RENEW,
          });
        }
      } else {
        updateFormState({
          billingFrequency: BillingFrequency.ANNUAL,
        });
      }
      updateHasAnnualLinkedEntitlements(true);
    }

    updateLinkedEntitlements({
      loadingLinkedEntitlements: false,
      linkedEntitlements: activeLinkedEntitlements,
    });
  };

  const { data: linkedEntitlementData, error: linkedEntitlementDataError } = useQuery<
    FetchLinkedEntitlementsForChangeSubcsriptionQuery,
    FetchLinkedEntitlementsForChangeSubcsriptionQueryVariables
  >(fetchLinkedEntitlementsForChangeSubcsription, {
    variables: {
      id: entitlement.id,
      skipUpgradeTrialField: isExistingAnnualPlanForJCA, //TODO remove this variable once upgrade trial queries are optimized in backend. Apps dont have upgrade trial so can skip fetching upgrade trials in Annual
      searchLinkedEntitlementParams: {
        entitlementId: entitlement.id,
        containerId: parentContainerID,
        productType: entitlement.productType,
        currentEntitlementType: currentEntitlementType(entitlement),
        destinationEntitlementType: [
          LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
          LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
          LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
          LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
        ],
        findApps: true,
        findProducts: true,
        checkForLastParentExistence: false,
      },
    },
    skip: isStatusPageProduct,
    displayName: 'fetchAllLinkedEntitlements',
    notifyOnNetworkStatusChange: true,
    onCompleted: onFetchLinkedEntitlementsCompleted,
    onError: () => updateLinkedEntitlements({ linkedEntitlements: [], loadingLinkedEntitlements: false }),
  });

  const linkedEntitlementInvoiceGroupId = useMemo(() => {
    return linkedEntitlementData?.entitlementDetails?.linkedEntitlements?.find(
      (entitlement) => !!entitlement?.billingGroupId
    )?.billingGroupId;
  }, [linkedEntitlementData]);

  useEffect(() => {
    if (linkedEntitlementInvoiceGroupId) {
      const linkedEntitlementIGDetails =
        availableInvoiceGroups.find((invoiceGroup) => invoiceGroup.id === linkedEntitlementInvoiceGroupId) || {};
      updateFormState({
        ...formState,
        invoiceGroupId: {
          ...formState.invoiceGroupId,
          value: linkedEntitlementIGDetails.id || '',
          invoiceGroup: linkedEntitlementIGDetails,
        },
      });
    }
  }, [linkedEntitlementInvoiceGroupId]);

  const [availableFilteredInvoiceGroups, defaultInvoiceGroup] = useMemo(() => {
    const filteredInvoiceGroups = linkedEntitlementInvoiceGroupId
      ? availableInvoiceGroups.filter((invoiceGroup) => invoiceGroup.id === linkedEntitlementInvoiceGroupId)
      : availableInvoiceGroups;
    return [
      filteredInvoiceGroups,
      linkedEntitlementInvoiceGroupId ? filteredInvoiceGroups[0] : currentInvoiceGroup || null,
    ];
  }, [linkedEntitlementInvoiceGroupId, availableInvoiceGroups]);

  const pricingEligibility = useMemo(() => {
    return getPricingEligibility({
      invoiceGroup: defaultInvoiceGroup,
      transactionAccount,
    });
  }, [defaultInvoiceGroup]);

  useEffect(() => {
    const fetchDataForLinkedEntitlements =
      isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL;

    const getAvailableOfferings = async () => {
      const promises = [];

      if (!availableOfferings.response?.[entitlement.id]) {
        promises.push(fetchAvailableOfferings(entitlement.id));
      }

      if (fetchDataForLinkedEntitlements) {
        filteredLinkedEntitlements?.forEach((linkedEntitlement) => {
          if (!availableOfferings.response?.[linkedEntitlement.entitlementId]) {
            promises.push(fetchAvailableOfferings(linkedEntitlement.entitlementId));
          }
        });
      }

      if (promises.length > 0) {
        updateAvailableOfferings({
          ...availableOfferings,
          loading: true,
        });

        const result = await Promise.all(promises);
        const updatedState: { [key: string]: Maybe<Array<Maybe<Offering>>> | undefined } = {};

        result?.forEach((item: ApolloQueryResult<FetchAvailableOfferingsForChangeSubscriptionQuery>) => {
          const data = item?.data?.entitlementDetails;
          updatedState[data?.id] = data?.availableOfferings;
        });

        updateAvailableOfferings({
          response: {
            ...availableOfferings.response,
            ...updatedState,
          },
          loading: false,
        });
      }
    };

    const getDefaultAPIData = async () => {
      const promises = [];

      if (!defaultApiData.response?.[formState.billingFrequency]?.[entitlement.offeringId as string]) {
        promises.push(
          getDefaultAPIEntitlementData({
            entitlement,
            entitlementId: entitlement.id,
            offeringId: entitlement.offeringId,
            billingCycle: formState.billingFrequency,
          })
        );
      }

      if (isBillingCycleChanged) {
        filteredLinkedEntitlements?.forEach((linkedEntitlement) => {
          if (
            !linkedEntitlement.isApp &&
            !defaultApiData.response?.[formState.billingFrequency]?.[linkedEntitlement.offeringId as string]
          ) {
            promises.push(
              getDefaultAPIEntitlementData({
                entitlement: linkedEntitlement,
                entitlementId: linkedEntitlement.entitlementId,
                offeringId: linkedEntitlement?.offeringId,
                billingCycle: formState.billingFrequency,
              })
            );
          }
        });
      }

      if (promises.length > 0) {
        updateDefaultApiData({
          ...defaultApiData,
          loading: true,
        });

        const result = await Promise.all(promises);
        const updatedState: { [key: string]: DefaultAPIResponse } = {};

        result?.forEach((item: DefaultAPIResponse) => {
          updatedState[item?.offeringId] = item;
        });

        updateDefaultApiData({
          response: {
            ...defaultApiData.response,
            [formState.billingFrequency]: updatedState,
          },
          loading: false,
        });
      }
    };

    const getAvailablePromotions = async () => {
      setAvailablePromotions(entitlement.entitlementNumber, formState.skipTrial); //for current entitlement

      if (fetchDataForLinkedEntitlements && filteredLinkedEntitlements?.length > 0) {
        try {
          setAvailablePromotionsData((promotionsData) => ({
            ...promotionsData,
            loading: { ALL: true },
            error: null,
          }));
          let availableLinkedPromotionsData = {};
          await Promise.all(
            filteredLinkedEntitlements.map(async (linkedEntitlement) => {
              if (linkedEntitlement.entitlementNumber) {
                const skipTrialValues = getDefaultSkipTrialValues(
                  linkedEntitlement,
                  isAnnualTermEnabled,
                  isBillingCycleChanged
                );
                const intendedBillingPeriodState = getIntendedBillingPeriodState(skipTrialValues.skipTrial);
                const promotions = await getAvailablePromotionsQuery(
                  linkedEntitlement.entitlementNumber,
                  intendedBillingPeriodState
                );
                availableLinkedPromotionsData = {
                  ...availableLinkedPromotionsData,
                  [linkedEntitlement.entitlementNumber]: promotions?.data?.entitlementDetails?.availablePromotions,
                };
              }
              return;
            })
          );
          setAvailablePromotionsData((promotionsData) => ({
            ...promotionsData,
            response: {
              ...promotionsData.response,
              ...availableLinkedPromotionsData,
            },
            loading: { ALL: false },
          }));
        } catch (error) {
          // TODO: handle error case
          setAvailablePromotionsData((promotionsData) => ({
            ...promotionsData,
            loading: { ALL: false },
            error,
          }));
        }
      }
    };

    const getPricingPlans = async () => {
      const promises = [];

      promises.push(
        fetchPricingPlans({
          entitlementNumber: entitlement.entitlementNumber,
          currency: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
          offeringId: entitlement.offeringId,
          planType: pricingEligibility,
          billingCycle: formState.billingFrequency,
        })
      );

      if (fetchDataForLinkedEntitlements) {
        filteredLinkedEntitlements?.forEach((linkedEntitlement) => {
          promises.push(
            fetchPricingPlans({
              entitlementNumber: linkedEntitlement.entitlementNumber,
              currency: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
              offeringId: linkedEntitlement.offeringId,
              planType: pricingEligibility,
              billingCycle: formState.billingFrequency,
            })
          );
        });
      }

      if (promises.length > 0) {
        updateAvailablePricingPlans({
          ...availablePricingPlans,
          loading: true,
        });

        const result = await Promise.all(promises);
        const updatedState: { [key: string]: Maybe<Array<Maybe<PricingPlan>>> | undefined } = {};

        result?.forEach((item: ApolloQueryResult<FetchAvailablePricingPlansQuery>) => {
          const data = item?.data?.entitlementDetails;

          updatedState[data?.id] = data?.availablePricingPlans?.filter(
            (pricingPlan) => !notActivePricingStatus.includes(pricingPlan?.status as string)
          );
        });

        updateAvailablePricingPlans({
          response: {
            ...availablePricingPlans.response,
            [formState.billingFrequency]: updatedState,
          },
          loading: false,
        });
      }
    };

    if (!loadingLinkedEntitlements) {
      getAvailableOfferings();
      getPricingPlans();
      getDefaultAPIData();
      getAvailablePromotions();
    }
  }, [isBillingCycleChanged, loadingLinkedEntitlements]);

  useEffect(() => {
    const defaultApiResult = defaultApiData.response?.[formState.billingFrequency];
    const availablePricingPlansData = availablePricingPlans.response?.[formState.billingFrequency];

    if (defaultApiResult && availablePricingPlansData) {
      const updatedFormState = getFormStateWithoutAddedPromotionFields(formState);
      let formValues: FormState = {
        ...updatedFormState,
        nextBillingAnchorTimestamp:
          isBillingCycleChanged && !isStatusPageProduct
            ? moment.utc(getCorrectedCurrentDateForAnnual(currentDate)).valueOf()
            : defaultApiResult[entitlement.offeringId as string]?.billingAnchorTime?.value,
      };

      if (isBillingCycleChanged) {
        formValues = getFormValuesForLinkedEntitlements({
          entitlement,
          linkedEntitlements,
          filteredLinkedEntitlements,
          formValues,
          availablePricingPlansData,
          defaultApiData: defaultApiResult,
          isAnnualTermEnabled,
          isBillingCycleChanged,
        });

        if (!isAnnualBillingCycleChanged) {
          const pricingPlans = availablePricingPlansData?.[entitlement.id];
          const defaultPricingPlanId = defaultApiResult?.[entitlement.offeringId as string]?.pricingPlanId?.value;
          const defaultPricingPlan = !defaultPricingPlanId
            ? getDefaultPricingPlan(pricingPlans)
            : pricingPlans?.find((item) => item?.id === defaultPricingPlanId);
          const pricingPlanOption = defaultPricingPlan?.id
            ? {
                label: getPricingPlanDescription(defaultPricingPlan),
                value: defaultPricingPlan.id,
                tiers: defaultPricingPlan.tiers,
                chargeElement: defaultPricingPlan.chargeElement,
                chargeType: defaultPricingPlan.chargeType,
                type: defaultPricingPlan.type,
                pricingPlanType: defaultPricingPlan.pricingPlanType,
              }
            : null;

          formValues = {
            ...formValues,
            offering: entitlement.offering?.id
              ? {
                  label: entitlement.offering.name,
                  value: entitlement.offering.id,
                  pricingType: entitlement.offering.pricingType,
                  level: entitlement.offering.level,
                  status: entitlement.offering.status,
                }
              : null,
            userTier: undefined,
            pricingPlan: pricingPlanOption,
            ...getDefaultSkipTrialValues(entitlement, isAnnualTermEnabled, isBillingCycleChanged),
          };

          if (!isStatusPageProduct && formState.billingFrequency === BillingFrequency.ANNUAL) {
            // eslint-disable-next-line max-depth
            if (entitlement.isApp) {
              formValues.userTier = getAppUserTierForBillingCycleChange({
                linkedEntitlements,
                availablePricingPlansData,
                defaultApiData: defaultApiResult,
                pricingPlanTiers: defaultPricingPlan?.tiers,
                activeUsers: entitlement.activeUsers,
              });
            } else {
              const chargeQuantity =
                defaultApiResult?.[entitlement.offeringId as string]?.chargeQuantities?.[0]?.quantity;
              const userTierOptions = getUserTierOptions({
                chargeQuantity,
                activeUsers: entitlement.activeUsers,
                userTiers: defaultPricingPlan?.tiers,
              }) as UserTierOption[];

              formValues.userTier = userTierOptions?.[0] ?? null;
            }
          }
        }

        updateFormStateValues(formValues);
      } else {
        if (formState.billingFrequency === BillingFrequency.ANNUAL) {
          formValues = getFormValuesForLinkedEntitlements({
            entitlement,
            linkedEntitlements,
            filteredLinkedEntitlements,
            formValues,
            availablePricingPlansData,
            defaultApiData: defaultApiResult,
            isAnnualTermEnabled,
            isBillingCycleChanged,
          });
        }

        const finalState = {
          ...formValues,
          ...getFormValuesForCurrentEntitlement({
            entitlement,
            linkedEntitlements,
            isAnnualTermEnabled,
            isBillingCycleChanged,
          }),
          coBillOption: false,
          coBillTimestamp: null,
        };
        setPristineFormState(finalState);
        updateFormStateValues(finalState);
      }

      setTierValidationState({});
      setTrialValidationMsg('');
    }
  }, [isBillingCycleChanged, defaultApiData.loading, availablePricingPlans.loading]);

  const setPartnerPromotion = async (
    entitlementNumber: EntitlementDetails['entitlementNumber'],
    skipTrial: FormState['skipTrial']
  ) => {
    if (entitlementNumber && isPartnerPromotionEnabled) {
      const intendedBillingPeriodState = getIntendedBillingPeriodState(skipTrial);
      try {
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          error: null,
          loading: { [entitlementNumber]: true },
        }));
        const partnerPromotionResponse = await getPartnerPromotionQuery({
          entitlementNumber,
          intendedBillingPeriodState,
          billingFrequency: formState.billingFrequency,
          discountTier: transactionAccount?.partnerDiscountTier,
          programLevelGlobal: transactionAccount?.programLevelGlobal,
        });
        const partnerPromotion = partnerPromotionResponse?.data?.entitlementDetails?.availablePartnerPromotions?.[0];
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          response: {
            ...(partnerPromotionData?.response || {}),
            [entitlementNumber]: partnerPromotion ?? null,
          },
        }));
      } catch (error) {
        // TODO: handle error case
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          error,
        }));
      } finally {
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          loading: { [entitlementNumber]: false },
        }));
      }
    }
  };

  const setAvailablePromotions = async (
    entitlementNumber: EntitlementDetails['entitlementNumber'],
    skipTrial: FormState['skipTrial']
  ) => {
    setPartnerPromotion(entitlementNumber, skipTrial);
    const intendedBillingPeriodState = getIntendedBillingPeriodState(skipTrial);
    if (entitlementNumber) {
      try {
        setAvailablePromotionsData((promotionsData) => ({
          ...promotionsData,
          error: null,
          loading: { [entitlementNumber]: true },
        }));
        const promotions = await getAvailablePromotionsQuery(entitlementNumber, intendedBillingPeriodState);
        setAvailablePromotionsData((promotionsData) => ({
          ...promotionsData,
          loading: { [entitlementNumber]: false },
          response: {
            ...(promotionsData?.response || {}),
            [entitlementNumber]: promotions?.data?.entitlementDetails?.availablePromotions,
          },
        }));
      } catch (error) {
        // TODO: handle error case
        setAvailablePromotionsData((promotionsData) => ({
          ...promotionsData,
          error: null,
          loading: { [entitlementNumber]: false },
        }));
      }
    }
  };

  const updateFormState = (updatedFormState: FormState) => {
    updateFormStateValues(cloneDeep(assign(formState, updatedFormState)));
  };

  const getPlanChangeType = () => {
    const selectedOffering = formState.offering;
    const offering = entitlement?.offering;
    if (!offering?.level || !selectedOffering?.level || offering?.level === selectedOffering?.level) return null;
    if (selectedOffering?.level > offering?.level) {
      return PlanType.UPGRADE;
    }
    return PlanType.DOWNGRADE;
  };

  const requestLinkedEntitlements = (formStateValues: FormState) => {
    return getLinkedEntitlementsDetails(
      entitlement,
      finalLinkedEntitlements.map((linkedEntitlement) => {
        const linkedEntitlementState = formStateValues[linkedEntitlement.entitlementId];

        const linkedEntitlementRequest = {
          ...linkedEntitlement,
          isExpiredPricingPlan: notActivePricingStatus.includes(linkedEntitlement?.pricingPlan?.status as string),
          pricingPlanType: linkedEntitlementState.pricingPlan?.type as string,
          pricingPlanId: linkedEntitlementState.pricingPlan?.value,
          chargeElement: linkedEntitlementState.pricingPlan?.chargeElement,
          chargeType: linkedEntitlementState.pricingPlan?.chargeType as string,
          offeringId: linkedEntitlementState.offering?.value,
          userTier: linkedEntitlementState.userTier?.value?.toString(),
          skipTrial: linkedEntitlementState.skipTrialEligible ? linkedEntitlementState.skipTrial : undefined,
          isImmediateAmendmentToFree:
            enableImmediateDowngradeForFreeEntitlement &&
            isImmediateAmendmentToFreeOrder(linkedEntitlement, linkedEntitlementState.offering),
          ...getPromotionsRequestBody({
            entitlement: linkedEntitlement,
            formStateValues: linkedEntitlementState,
            patchRemovePromotionEnabled,
          }),
        };

        return linkedEntitlementRequest;
      }),
      formStateValues.offering?.value
    );
  };

  const buildRequest = (formData: FormState) => {
    const updatedFormState = cloneDeep(formState);
    const formStateValues: FormState = assign(updatedFormState, formData);
    let request: UpdateOrderRequest = {
      isBillingCycleChanged: isBillingCycleChanged && !isStatusPageProduct,
      entitlementId: entitlement.id,
      billingFrequency: formStateValues.billingFrequency,
      units: (isStatusPageProduct ? formStateValues.units : formStateValues.userTier?.value?.toString()) ?? '1',
      isPromotionStackingAllowed: true,
      invoiceGroupId: defaultTo(formStateValues.invoiceGroupId?.value, ''),
      offeringId: formStateValues.offering?.value,
      orderType: OrderItemType.AMENDMENT_ORDER,
      productId: entitlement.productId as string,
      //@ts-ignore
      chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
      pricingPlanType: formStateValues.pricingPlan?.type as string,
      pricingPlanId: formStateValues?.pricingPlan?.value,
      chargeElement: formStateValues?.pricingPlan?.chargeElement ?? getChargeElement(entitlement?.productId as string),
      chargeType: formStateValues?.pricingPlan?.chargeType as string,
      linkedEntitlements:
        (isBillingCycleChanged || formStateValues.billingFrequency === BillingFrequency.ANNUAL) &&
        finalLinkedEntitlements?.length > 0
          ? requestLinkedEntitlements(formStateValues)
          : [],
      isImmediateAmendmentToFree:
        enableImmediateDowngradeForFreeEntitlement &&
        isImmediateAmendmentToFreeOrder(entitlement, formStateValues.offering as OfferingOption),
    };

    //To check that the pricing plan status while amending an order
    request.isExpiredPricingPlan = notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string);
    request = {
      ...request,
      ...getPromotionsRequestBody({ entitlement, formStateValues, patchRemovePromotionEnabled }),
      metadata: {
        [OMSMetadata.SUPPORT_TICKET_NUMBER]: formStateValues.supportTicketLink
          ? isValidURL(formStateValues.supportTicketLink)
            ? formStateValues.supportTicketLink
            : `${gsacBaseUrl}/browse/${formStateValues.supportTicketLink}`
          : null,
        [OMSMetadata.HAND_NOTE]: defaultTo(formStateValues.notes, null),
        [OMSMetadata.CONSENT_TAKEN]: defaultTo(formStateValues.consentTaken?.toString(), null),
      },
    };

    if (formStateValues.skipTrialEligible) {
      request.skipTrial = formStateValues.skipTrial;
    }

    if (formStateValues?.immediateAmendment) {
      request.isImmediate = formStateValues.immediateAmendment;
    }

    let endTimestampValue = formStateValues?.endTimestamp;
    let billingAnchorTimestampValue = formStateValues?.nextBillingAnchorTimestamp;

    if (formStateValues.coBillTimestamp) {
      if (endTimestampValue) {
        endTimestampValue = formStateValues.coBillTimestamp;
      } else {
        billingAnchorTimestampValue = formStateValues.coBillTimestamp;
      }
    }

    if (endTimestampValue) {
      request.endsAt = {
        type: EndsAtType.TIMESTAMP,
        value: endTimestampValue,
      };
    }

    if (billingAnchorTimestampValue) {
      request.nextBillingAnchorTimestamp = `${billingAnchorTimestampValue}`;
    }

    if (isOmsServiceV2Enabled) {
      const entitlementsList = [
        {
          entitlementId: entitlement.id,
          productId: entitlement.productId as string,
          offeringId: formStateValues.offering?.value,
          chargeQuantity: getChargeQuantity({
            formStateValues,
            entitlement,
            billingFrequency: formStateValues.billingFrequency,
          }),
          pricingPlanType: formStateValues.pricingPlan?.type as string,
          skipTrial: formStateValues?.skipTrial ? formStateValues.skipTrial : undefined,
          isExpiredPricingPlan: notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string),
          ...getPromotionsRequestBody({ entitlement, formStateValues, patchRemovePromotionEnabled }),
          ...getChargingDetails(formStateValues, entitlement),
          isImmediateAmendmentToFree:
            enableImmediateDowngradeForFreeEntitlement &&
            isImmediateAmendmentToFreeOrder(entitlement, formStateValues.offering as OfferingOption),
          ...getCommonEntitlementDetails(formStateValues),
        },
      ];

      if (
        (isBillingCycleChanged || formStateValues.billingFrequency === 'ANNUAL') &&
        finalLinkedEntitlements?.length > 0
      ) {
        finalLinkedEntitlements.forEach((linkedEntitlement) => {
          const linkedEntitlementState = formStateValues[linkedEntitlement.entitlementId];
          const linkedEntitlementRequest = {
            entitlementId: linkedEntitlement.entitlementId,
            productId: linkedEntitlement.productId as string,
            isExpiredPricingPlan: notActivePricingStatus.includes(linkedEntitlement?.pricingPlan?.status as string),
            pricingPlanType: linkedEntitlementState.pricingPlan?.type as string,
            ...getChargingDetails(linkedEntitlementState, linkedEntitlement),
            offeringId: linkedEntitlementState.offering?.value,
            chargeQuantity: getChargeQuantity({
              formStateValues: linkedEntitlementState,
              entitlement: linkedEntitlement,
              billingFrequency: formStateValues.billingFrequency,
            }),
            skipTrial: linkedEntitlementState.skipTrialEligible ? linkedEntitlementState.skipTrial : undefined,
            isImmediateAmendmentToFree:
              enableImmediateDowngradeForFreeEntitlement &&
              isImmediateAmendmentToFreeOrder(linkedEntitlement, linkedEntitlementState.offering),
            ...getPromotionsRequestBody({
              entitlement: linkedEntitlement,
              formStateValues: linkedEntitlementState,
              patchRemovePromotionEnabled,
            }),
            ...getCommonEntitlementDetails(formStateValues),
          };

          entitlementsList.push(linkedEntitlementRequest);
        });
      }

      const constructV2Request = {
        orderType: OrderItemType.AMENDMENT_ORDER,
        metadata: {
          [OMSMetadata.SUPPORT_TICKET_NUMBER]: formStateValues.supportTicketLink
            ? isValidURL(formStateValues.supportTicketLink)
              ? formStateValues.supportTicketLink
              : `${gsacBaseUrl}/browse/${formStateValues.supportTicketLink}`
            : null,
          [OMSMetadata.HAND_NOTE]: defaultTo(formStateValues.notes, null),
          [OMSMetadata.CONSENT_TAKEN]: defaultTo(formStateValues.consentTaken?.toString(), null),
        },
        invoiceGroupId: defaultTo(formStateValues.invoiceGroupId?.value, undefined),
        billingFrequency: formStateValues.billingFrequency,
        entitlements: entitlementsList,
        transactionAccountId: entitlement?.transactionAccountId as string,
      };
      return constructCustomOrderRequest(constructV2Request);
    }

    return request;
  };

  const calculateAndSendTimeMetric = () => {
    logTimeStamp(new Date().getTime(), 'Change Subscription', SignalFxFEPerfStore.FE_USER_TASK_CHANGE_SUB_TIME);
  };

  const buildApprovalEntityInformation = (approvalType: ApprovalType, toPricingPlanType: Maybe<string> = '') => {
    const defaultEntity = {
      entityType: EntityType.ENTITLEMENT,
      entityId: entitlement.id,
      entityNumber: entitlement.entitlementNumber,
      discountPercentage: customDiscount,
      discountValue: discretionaryEstimate?.discretionaryDiscountAmount ?? 0,
    };
    return [
      {
        ...defaultEntity,
        ...(approvalType === ApprovalType.PRICING_PLAN_CHANGE
          ? {
              fromPricingPlanKey: entitlement?.pricingPlan?.pricingPlanType ?? '',
              toPricingPlanKey: toPricingPlanType,
            }
          : {}),
      },
    ];
  };

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Approval request submitted')
      .withMessage('You can view your request in the Approval requests section.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const {
    approvalRequired: isApprovalRequiredForDiscount,
    approvalError: approvalErrorForDiscount,
    approvalList: approvalListForDiscount,
    approvalProcessing: approvalProcessingForDiscount,
    evaluate: evaluateApprovalForDiscount,
    setApprovalRequired: setApprovalRequiredDiscount,
    approvalSlackChannel: approvalSlackChannelForDiscount,
    evaluateApprovalProcessing: evaluateDiscountApprovalProcessing,
  } = useApprovals({
    approvalType: ApprovalType.ORDER_DISCOUNT,
    onApprovalComplete,
  });
  const {
    approvalRequired: isApprovalRequiredForPricing,
    approvalError: approvalErrorForPricing,
    approvalList: approvalListForPricing,
    approvalProcessing: approvalProcessingForPricing,
    evaluate: evaluateApprovalForPricing,
    setApprovalRequired: setApprovalRequiredPricing,
    submitApproval,
    approvalSlackChannel: approvalSlackChannelForPricing,
    evaluateApprovalProcessing: evaluatePricingPlanApprovalProcessing,
  } = useApprovals({
    approvalType: ApprovalType.PRICING_PLAN_CHANGE,
    onApprovalComplete,
  });
  const actualApprovalType = useMemo(() => {
    return isApprovalRequiredForPricing ? ApprovalType.PRICING_PLAN_CHANGE : ApprovalType.ORDER_DISCOUNT;
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);
  const approvalRequiredForTypes = useMemo(() => {
    return isApprovalRequiredForPricing && isApprovalRequiredForDiscount
      ? [ApprovalType.ORDER_DISCOUNT, ApprovalType.PRICING_PLAN_CHANGE]
      : [];
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);
  const approvalList = useMemo(() => {
    if (isApprovalRequiredForDiscount && isApprovalRequiredForPricing) {
      return [
        ...approvalListForDiscount.filter((item) => item.approvalRequired),
        ...approvalListForPricing.filter((item) => item.approvalRequired),
      ];
    }
    if (isApprovalRequiredForDiscount) {
      return approvalListForDiscount.filter((item) => item.approvalRequired);
    }
    if (isApprovalRequiredForPricing) {
      return approvalListForPricing.filter((item) => item.approvalRequired);
    }
    return [];
  }, [approvalListForDiscount, approvalListForPricing, isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const validateInvoiceGroupChange = (termPaymentResponse: creditPolicyResponse) => {
    setIsDeferredPaymentIGSelectionBlocked(false);
    if (
      isAnnualTermedSelected &&
      isCreditManagementEnabled &&
      termPaymentResponse?.status === txaPushPaymentStatus.INELIGIBLE
    ) {
      setIsDeferredPaymentIGSelectionBlocked(true);
    }
  };

  // Performs finance policy validation to support term payment for annual termed subscription.
  const checkFinancePolicy = async (formData: FormState) => {
    try {
      setTermPaymentValidation({ loading: true });
      let financePayload = buildRequest(formData) as UpdateOrderRequest;
      financePayload = {
        ...financePayload,
        skipTrial: formState.skipTrialEligible ? true : undefined,
        linkedEntitlements: financePayload?.linkedEntitlements?.map((item, index) => {
          const linkedEntitlementFormState = formState[item.entitlementId as keyof FormState] as FormState;
          const updatedItem = {
            ...item,
          };
          // first item is the current entitlement so ignoring first index as setting true at root level for it
          if (index !== 0) {
            updatedItem.skipTrial = linkedEntitlementFormState?.skipTrialEligible ? true : undefined;
          }

          return updatedItem;
        }) as LinkedEntitlementsInstance[],
      };
      const termPaymentResponse = await termPaymentVerification(financePayload);
      validateInvoiceGroupChange(termPaymentResponse);
      setTermPaymentValidation({ loading: false, response: termPaymentResponse });
    } catch (e) {
      setTermPaymentValidation({
        loading: false,
        response: { status: termPaymentValidation.response?.status as txaPushPaymentStatus },
      });
    }
  };

  const handleFormSubmit = async (formData: FormState) => {
    setIsBillingProfileInValid(false);
    let updatedFormData = formData;
    if (currentProgressIndex !== FormTrackerStep.PLAN_DETAILS) {
      updatedFormData = getFormStateWithoutAddedPromotionFields(formData);

      Object.keys(formData)?.forEach((key) => {
        const isLinkedEntitlement = filteredLinkedEntitlements?.some((item) => item.entitlementId === key);
        if (isLinkedEntitlement) {
          delete updatedFormData[key];
        }
      });
    }

    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_CYCLE: {
        updateFormState(updatedFormData);
        setCurrentProgressIndex(currentProgressIndex + 1);
        break;
      }
      case FormTrackerStep.PLAN_DETAILS: {
        const updatedFormState = getFormStateWithoutRemovedPromotionFields(formState, formData);

        Object.keys(formData)?.forEach((key) => {
          const isLinkedEntitlement = filteredLinkedEntitlements?.some((item) => item.entitlementId === key);
          if (isLinkedEntitlement) {
            updatedFormState[key] = {
              ...getFormStateWithoutRemovedPromotionFields(updatedFormState[key], formData[key]),
              ...formData[key],
            };
          } else {
            updatedFormState[key] = formData[key];
          }
        });
        let errorMsg = '';
        const cutShortTrialErrorMsg = 'Can not change offering, pricing plan or user tier while cutting short a trial';
        const isFormDirty = checkisFormDirty({
          entitlement,
          updatedFormState,
          pristineFormState,
          isBillingCycleChanged,
        });
        if (isFormDirty) {
          if (
            isExistingAnnualPlanForJCA &&
            updatedFormState.hasOptedForUpgradeTrial &&
            pristineFormState?.userTier?.value !== formState?.userTier?.value
          ) {
            errorMsg = 'Can not change user tier when opting for upgrade trial';
          } else if (
            isInvalidCutShortTrial({
              updatedFormState,
              pristineFormState: pristineFormState as FormState,
              entitlementDetails: entitlement,
            })
          ) {
            errorMsg = cutShortTrialErrorMsg;
          }

          if (filteredLinkedEntitlements?.length > 0) {
            const result = getFinalLinkedEntitlements({
              updatedFormState,
              pristineFormState,
              filteredLinkedEntitlements,
              isBillingCycleChanged,
            });

            if (!errorMsg) {
              result?.forEach((linkedEnt) => {
                if (
                  isInvalidCutShortTrial({
                    updatedFormState: updatedFormState?.[linkedEnt?.entitlementId],
                    pristineFormState: pristineFormState?.[linkedEnt?.entitlementId],
                    entitlementDetails: linkedEnt,
                  })
                ) {
                  errorMsg = cutShortTrialErrorMsg;
                }
              });
            }
            updateFinalLinkedEntitlements(result);
          }
        }

        setIsFormDirty(isFormDirty);
        updateFormStateValues(cloneDeep(updatedFormState));
        setTrialValidationMsg(errorMsg);
        isFormDirty && !errorMsg && setCurrentProgressIndex(currentProgressIndex + 1);
        break;
      }
      case FormTrackerStep.BILLING_PROFILE: {
        updateFormState(updatedFormData);
        setCurrentProgressIndex(currentProgressIndex + 1);
        break;
      }
      case FormTrackerStep.REVIEW: {
        calculateAndSendTimeMetric();
        if (
          (grandTotal ?? 0) > 0 &&
          (!validateBillToAddress ||
            !defaultTo(updatedFormData?.invoiceGroupId?.invoiceGroup?.defaultPaymentMethod, null))
        ) {
          setIsBillingProfileInValid(true);
          return;
        }

        const request = buildRequest(updatedFormData);
        if (isApprovalRequiredForDiscount || isApprovalRequiredForPricing) {
          submitRequestAfterApprovalCancelation(
            () =>
              submitApproval({
                approvalType: actualApprovalType,
                businessKey: entitlement.entitlementNumber as string,
                entityInformation: buildApprovalEntityInformation(
                  actualApprovalType,
                  formState?.pricingPlan?.pricingPlanType
                ),
                gsacId: updatedFormData.supportTicketLink,
                initiatorComments: updatedFormData.comments,
                additionalInformation: request,
              }),
            hasOpenApprovalRequest
          );
        } else {
          submitRequestAfterApprovalCancelation(() => submitForm(request), hasOpenApprovalRequest);
        }
      }
    }
  };

  const handleCustomPromotionValueChange = React.useCallback(({ discountPercentage }) => {
    // as only one discretionary discount is allowed, considering the last one in the stack
    setCustomDiscount(discountPercentage);
  }, []);

  const isApprovalFormRequired = React.useCallback(() => {
    return (
      (isApprovalRequiredForPricing || isApprovalRequiredForDiscount) &&
      !discretionaryEstimate?.loading &&
      !(discretionaryEstimate?.error && discreteDiscountPromotions.length) &&
      !discretionaryEstimate?.isDisabled &&
      currentProgressIndex === FormTrackerStep.REVIEW
    );
  }, [isApprovalRequiredForPricing, isApprovalRequiredForDiscount, discretionaryEstimate, currentProgressIndex]);

  const hasPaidAppSandboxEntitlement = React.useMemo(() => {
    return [entitlement, ...linkedEntitlements]?.some((ent) => {
      return ent?.isSandbox && ent?.isApp && ent?.offering?.pricingType === PricingType.PAID;
    });
  }, [linkedEntitlements, entitlement]);

  const isBillingCycleDisabled = React.useMemo(() => {
    return availableOfferings.loading || availablePricingPlans.loading || defaultApiData.loading;
  }, [availableOfferings.loading, availablePricingPlans.loading, defaultApiData.loading]);

  const isPricingEligibilityMismatching = useMemo(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW) {
      if (formState?.invoiceGroupId?.value) {
        const invoiceGroup = availableInvoiceGroups.find((ig) => ig?.id === formState?.invoiceGroupId?.value);
        const shipToPriceEligibility = invoiceGroup?.shipToPriceEligibility;
        if (shipToPriceEligibility) {
          const entPricingPlanType = formState?.pricingPlan?.type;
          if (entPricingPlanType && entPricingPlanType !== shipToPriceEligibility.toUpperCase()) return true;
          if (finalLinkedEntitlements?.length) {
            return finalLinkedEntitlements.some((linkedEnt) => {
              const linkedEntPricingPlanType = formState?.[linkedEnt?.entitlementId]?.pricingPlan?.type;
              if (linkedEntPricingPlanType && linkedEntPricingPlanType !== shipToPriceEligibility.toUpperCase()) {
                return true;
              }
              return false;
            });
          }
        }
      }
    }
    return false;
  }, [currentProgressIndex, formState, finalLinkedEntitlements, availableInvoiceGroups]);

  const isSubmitButtonDisabled = React.useCallback(() => {
    return (
      (currentProgressIndex === FormTrackerStep.BILLING_CYCLE &&
        isAnnualTermEnabled &&
        isAnnualSelected &&
        !(isAnnualAutoRenewSelected || isAnnualTermedSelected) &&
        isBillingCycleChanged) ||
      isPricingEligibilityMismatching ||
      (currentProgressIndex === FormTrackerStep.REVIEW && (orderPreviewData.loading || !!orderPreviewData.error)) ||
      !!taskListError ||
      evaluateDiscountApprovalProcessing ||
      evaluatePricingPlanApprovalProcessing ||
      discretionaryEstimate.loading ||
      (!!discretionaryEstimate?.error && !!discreteDiscountPromotions.length) ||
      discretionaryEstimate?.isDisabled ||
      !!approvalErrorForPricing ||
      loadingLinkedEntitlements ||
      (hasPaidAppSandboxEntitlement && formState.billingFrequency === BillingFrequency.ANNUAL) ||
      isBillingCycleDisabled ||
      (currentProgressIndex === FormTrackerStep.PLAN_DETAILS &&
        (isRefetchingData || !tierChangeAllowed || partnerPromotionData?.loading?.ALL)) ||
      noActiveOfferingForBillingCycleChange ||
      (currentProgressIndex === FormTrackerStep.BILLING_PROFILE &&
        (termPaymentValidation.loading ||
          isDeferredPaymentIGSelectionBlocked ||
          (formState.coBillOption && !formState.coBillTimestamp)))
    );
  }, [
    isPricingEligibilityMismatching,
    taskListError,
    evaluateDiscountApprovalProcessing,
    evaluatePricingPlanApprovalProcessing,
    discretionaryEstimate,
    loadingLinkedEntitlements,
    currentProgressIndex,
    orderPreviewData,
    hasPaidAppSandboxEntitlement,
    formState.billingFrequency,
    isBillingCycleDisabled,
    isRefetchingData,
    noActiveOfferingForBillingCycleChange,
    tierChangeAllowed,
    partnerPromotionData,
    isDeferredPaymentIGSelectionBlocked,
    termPaymentValidation,
    formState.coBillOption,
    formState.coBillTimestamp,
  ]);

  if (complete && !notificationSent) {
    const isTrialToTrialConversion = !formState.skipTrial && isCurrentEntitlementOnTrial;
    const notificationConfig = buildMessage({
      response,
      isApprovalRequired:
        approvalEval.current &&
        (approvalEval.current?.discountApproval?.isApprovalRequired ||
          approvalEval.current?.pricePlanChangeApproval?.isApprovalRequired),
      isTrialToTrialConversion,
      hasOptedForUpgradeTrial: formState.hasOptedForUpgradeTrial,
    });
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('change-subscription-success')
      .withHeader(notificationConfig.header)
      .withMessage(notificationConfig.message)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    analytics.sendTrackEvent({
      attributes: {
        changeType: getPlanChangeType(),
      },
      action: AnalyticsTrackAction.UPDATED,
      actionSubject: AnalyticsTrackSubject.CHANGE_PLAN,
    });
    closeSidebar();
    refetch();
  }

  const stages = isAnnualTermEnabled ? Object.values(FormStagesV2) : Object.values(FormStages);
  const items: Stages = stages.map((stage, index) => ({
    id: `${index}`,
    label: stage,
    percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
    status:
      currentProgressIndex > index
        ? TrackerState.VISITED
        : currentProgressIndex === index
        ? TrackerState.CURRENT
        : TrackerState.UNVISITED,
    onClick: () => setCurrentProgressIndex(index),
  }));

  const renderFormStep = () => {
    switch (currentProgressIndex) {
      case FormTrackerStep.BILLING_CYCLE: {
        if (loadingLinkedEntitlements) {
          return <LoadingIndicator />;
        }
        return (
          <ManageBillingCycle
            entitlement={entitlement}
            formState={formState}
            isBillingCycleChanged={isBillingCycleChanged}
            linkedEntitlementsData={linkedEntitlementData}
            linkedEntitlementsLoading={loadingLinkedEntitlements}
            linkedEntitlementsError={linkedEntitlementDataError}
            hasAnnualLinkedEntitlements={hasAnnualLinkedEntitlements}
            updateFormState={updateFormState}
            isMonthlySelected={isMonthlySelected}
            isAnnualSelected={isAnnualSelected}
            isAnnualTermedSelected={isAnnualTermedSelected}
            isDisabled={isBillingCycleDisabled}
            isAnnualAutoRenewSelected={isAnnualAutoRenewSelected}
            currentDate={currentDate}
          />
        );
      }
      case FormTrackerStep.PLAN_DETAILS:
        if (
          availableOfferings.loading ||
          availablePricingPlans.loading ||
          defaultApiData.loading ||
          partnerPromotionData?.loading?.ALL
        ) {
          return <LoadingIndicator />;
        }
        return (
          <ManagePlanV2
            availablePricingPlans={availablePricingPlans}
            availableOfferings={availableOfferings}
            formState={formState}
            entitlement={entitlement}
            linkedEntitlements={linkedEntitlements}
            filteredLinkedEntitlements={filteredLinkedEntitlements}
            isBillingCycleChanged={isBillingCycleChanged}
            availablePromotionsData={availablePromotionsData}
            partnerPromotionData={partnerPromotionData}
            handleCustomPromotionValueChange={handleCustomPromotionValueChange}
            setAvailablePromotions={setAvailablePromotions}
            updateFormState={updateFormState}
            updateFormStateValues={updateFormStateValues}
            isAnnualBillingCycleChanged={isAnnualBillingCycleChanged}
            currentInvoiceGroup={currentInvoiceGroup}
            updateDefaultApiData={updateDefaultApiData}
            updateAvailablePricingPlans={updateAvailablePricingPlans}
            defaultApiData={defaultApiData}
            pricingEligibility={pricingEligibility}
            refetchLoadingState={refetchLoadingState}
            updateRefetchLoadingState={updateRefetchLoadingState}
            setIsFormDirty={setIsFormDirty}
            setTierValidationState={setTierValidationState}
            tierValidationState={tierValidationState}
          />
        );
      case FormTrackerStep.BILLING_PROFILE: {
        return (
          <ManageBillingProfile
            entitlement={entitlement}
            availableInvoiceGroups={availableFilteredInvoiceGroups}
            allAvailableInvoiceGroups={availableInvoiceGroups}
            formState={formState}
            updateFormState={updateFormState}
            linkedEntitlementInvoiceGroupId={linkedEntitlementInvoiceGroupId}
            termPaymentValidation={termPaymentValidation}
            setTermPaymentValidation={setTermPaymentValidation}
            linkedEntitlements={finalLinkedEntitlements}
            isBillingCycleChanged={isBillingCycleChanged}
            setGrandTotal={setGrandTotal}
            isAnnualTermedSelected={isAnnualTermedSelected}
            isAnnualSelected={isAnnualSelected}
            isDeferredPaymentIGSelectionBlocked={isDeferredPaymentIGSelectionBlocked}
            setIsDeferredPaymentIGSelectionBlocked={setIsDeferredPaymentIGSelectionBlocked}
            coBillData={coBillData}
            setCoBillData={setCoBillData}
            checkFinancePolicy={checkFinancePolicy}
            estimatedBill={estimatedBill}
            setEstimatedBill={setEstimatedBill}
            onDiscretionaryEstimateChange={setDiscretionaryEstimate}
            creditDetails={creditDetails}
          />
        );
      }
      case FormTrackerStep.REVIEW: {
        return (
          <ReviewChangeV2
            availableInvoiceGroups={availableFilteredInvoiceGroups}
            allAvailableInvoiceGroups={availableInvoiceGroups}
            formState={formState}
            entitlement={entitlement}
            updateFormState={updateFormState}
            onDiscretionaryEstimateChange={setDiscretionaryEstimate}
            linkedEntitlementInvoiceGroupId={linkedEntitlementInvoiceGroupId}
            linkedEntitlements={finalLinkedEntitlements}
            setGrandTotal={setGrandTotal}
            isBillingCycleChanged={isBillingCycleChanged}
            isMonthlySelected={isMonthlySelected}
            isAnnualTermedSelected={isAnnualTermedSelected}
            orderPreviewData={orderPreviewData}
            setOrderPreviewdata={setOrderPreviewdata}
            isAnnualSelected={isAnnualSelected}
            currentDate={currentDate}
            coBillData={coBillData}
            estimatedBill={estimatedBill}
            setEstimatedBill={setEstimatedBill}
            creditDetails={creditDetails}
          />
        );
      }
    }
  };
  useEffect(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW && !skipApproval) {
      (async () => {
        if (typeof discretionaryEstimate?.discretionaryDiscountAmount === 'number') {
          if (
            (isMonthlySelected || isAnnualDiscountApprovalFlowEnabled) &&
            isDiscountApprovalFlowEnabled &&
            discreteDiscountPromotions.length
          ) {
            const discountApproval: any = await evaluateApprovalForDiscount(
              buildApprovalEntityInformation(ApprovalType.ORDER_DISCOUNT)
            );
            approvalEval.current = { discountApproval };
          }
        }
      })();
    } else if (currentProgressIndex !== FormTrackerStep.BILLING_PROFILE) {
      setDiscretionaryEstimate({ loading: false });
      setApprovalRequiredDiscount(false);
      setApprovalRequiredPricing(false);
    }
  }, [
    currentProgressIndex,
    discreteDiscountPromotions,
    discretionaryEstimate?.discretionaryDiscountAmount,
    isDiscountApprovalFlowEnabled,
    isAnnualDiscountApprovalFlowEnabled,
    isMonthlySelected,
    skipApproval,
  ]);

  useEffect(() => {
    if (currentProgressIndex === FormTrackerStep.REVIEW && !skipApproval) {
      (async () => {
        if (
          (isMonthlySelected || isAnnualPricingPlanChangeApprovalFlowEnabled) &&
          isPricingPlanChangeApprovalFlowEnabled &&
          formState.pricingPlan?.pricingPlanType !== entitlement.pricingPlan?.pricingPlanType
        ) {
          const pricePlanChangeApproval: any = await evaluateApprovalForPricing(
            buildApprovalEntityInformation(ApprovalType.PRICING_PLAN_CHANGE, formState.pricingPlan?.pricingPlanType)
          );
          approvalEval.current = { pricePlanChangeApproval };
        }
      })();
    }
  }, [
    currentProgressIndex,
    formState.pricingPlan?.pricingPlanType,
    isPricingPlanChangeApprovalFlowEnabled,
    isAnnualPricingPlanChangeApprovalFlowEnabled,
    isMonthlySelected,
    skipApproval,
  ]);

  useEffect(() => {
    const fetchAllPartnerPromotions = async () => {
      const fetchDataForLinkedEntitlements =
        isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL;
      const allEntitlements: (EntitlementDetails | LinkedDestinationEntitlement)[] = [entitlement];
      if (fetchDataForLinkedEntitlements && filteredLinkedEntitlements?.length > 0) {
        allEntitlements.push(...filteredLinkedEntitlements);
      }
      try {
        setPartnerPromotionData((partnerPromotionData) => ({
          ...partnerPromotionData,
          loading: { ALL: true },
          error: null,
        }));
        let availablePartnerPromotions = {};
        await Promise.all(
          allEntitlements.map(async (entitlement) => {
            if (entitlement.entitlementNumber) {
              const skipTrialValues = getDefaultSkipTrialValues(
                entitlement,
                isAnnualTermEnabled,
                isBillingCycleChanged
              );
              const intendedBillingPeriodState = getIntendedBillingPeriodState(skipTrialValues.skipTrial);
              const partnerPromotionData = await getPartnerPromotionQuery({
                entitlementNumber: entitlement.entitlementNumber,
                intendedBillingPeriodState,
                billingFrequency: formState.billingFrequency,
                discountTier: transactionAccount?.partnerDiscountTier,
                programLevelGlobal: transactionAccount?.programLevelGlobal,
              });
              const partnerPromotion = partnerPromotionData?.data?.entitlementDetails?.availablePartnerPromotions?.[0];
              availablePartnerPromotions = {
                ...availablePartnerPromotions,
                [entitlement.entitlementNumber]: partnerPromotion ?? null,
              };
            }
            return;
          })
        );
        setPartnerPromotionData((promotionsData) => ({
          ...promotionsData,
          response: {
            ...promotionsData.response,
            ...availablePartnerPromotions,
          },
        }));
      } catch (error) {
        // TODO: handle error case
        setPartnerPromotionData((promotionsData) => ({
          ...promotionsData,
          error,
        }));
      } finally {
        setPartnerPromotionData((promotionsData) => ({
          ...promotionsData,
          loading: { ALL: false },
        }));
      }
    };
    if (currentProgressIndex === FormTrackerStep.PLAN_DETAILS && isPartnerPromotionEnabled) {
      fetchAllPartnerPromotions();
    }
  }, [currentProgressIndex]);

  const [isAppArchivedWarningShown, isAppAtNoticeWarningShown] = React.useMemo(() => {
    if (!entitlement.isApp) return [false, false];

    const selectedOffering = formState.offering;
    if (selectedOffering?.value) {
      if (selectedOffering?.value === entitlement.offeringId) {
        return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
      }

      return [
        selectedOffering?.status === OfferingStatus.EXPIRED,
        selectedOffering?.status === OfferingStatus.AT_NOTICE,
      ];
    }
    return [isOfferingArchived(entitlement), isOfferingAtNotice(entitlement)];
  }, [formState.offering]);
  const showOptedForUpgradeTrialBanner = useMemo(() => {
    return (
      currentProgressIndex === FormTrackerStep.PLAN_DETAILS &&
      (!!formState.hasOptedForUpgradeTrial ||
        filteredLinkedEntitlements.some((item) => !!formState[item.entitlementId]?.hasOptedForUpgradeTrial))
    );
  }, [currentProgressIndex, filteredLinkedEntitlements, formState]);
  const showEndUpgradeTrialBanner = useMemo(() => {
    const linkedEnts =
      currentProgressIndex === FormTrackerStep.REVIEW ? finalLinkedEntitlements : filteredLinkedEntitlements;

    return (
      currentProgressIndex !== FormTrackerStep.BILLING_CYCLE &&
      (!!entitlement.upgradeTrial ||
        ((isAnnualSelected || isBillingCycleChanged) && linkedEnts.some((item) => !!item.upgradeTrial)))
    );
  }, [currentProgressIndex, filteredLinkedEntitlements, entitlement]);

  const trackChangePlanAnalytics = () => {
    if (currentProgressIndex === FormTrackerStep.REVIEW) {
      sendAnalyticsEvent(AnalyticsTrackAction.CHANGED, AnalyticsTrackSubject.SUBSCRIPTION, {
        billingCycle: isAnnualSelected ? 'Annual' : 'Monthly',
        // @ts-ignore
        entitlement,
      });
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <form {...formProps}>
          <div className="header">
            <FormHeader title="Change subscription">
              <EntitlementIcon
                logoId={entitlement?.logoId as string}
                productId={entitlement?.productId as string}
                isCloud={true}
                className="avatar medium"
              />{' '}
              <h4 style={{ display: 'inline' }}>{`${defaultTo(entitlement?.productName, 'Unnamed product')} ${
                entitlement?.entitlementNumber
              }`}</h4>
              &nbsp;&nbsp;
              {entitlement?.scheduledChanges !== null && <Lozenge appearance="inprogress">changes scheduled</Lozenge>}
              &nbsp;&nbsp;
              {entitlement.pricingPlan?.type && (
                <Lozenge appearance="default">
                  {capitalize(startCase(entitlement?.pricingPlan?.type + ' ' + 'pricing'))}
                </Lozenge>
              )}
              <div className="header-border"></div>
            </FormHeader>
          </div>
          {showOptedForUpgradeTrialBanner && (
            <FormSection>
              <SectionMessage appearance="warning" testId="upgrade-trial-with-discount-banner">
                <p>No discounts can be added or removed to the base plan during an ongoing no-cost premium trial.</p>
              </SectionMessage>
            </FormSection>
          )}
          {showEndUpgradeTrialBanner && (
            <FormSection>
              <SectionMessage appearance="warning" testId="end-no-cost-premium-banner">
                <p>
                  Performing this action will end the no-cost Premium trial and the plan will be upgraded immediately.
                  The customer will be charged pro rata till the next bill date.
                </p>
              </SectionMessage>
            </FormSection>
          )}
          <FormSection>
            {!!taskListError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={taskListError.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {hasPaidAppSandboxEntitlement && formState.billingFrequency === BillingFrequency.ANNUAL && (
              <SectionMessage appearance="error">
                <p>
                  We do not support the movement or amendment of subscriptions with paid sandbox apps to the annual
                  cycle, at the moment.
                </p>
              </SectionMessage>
            )}
          </FormSection>
          <FormSection>
            {isAppArchivedWarningShown && (
              <InfoPanel
                type="warning"
                title="This app has been archived"
                message="The subscription can only be moved to an active offering."
              />
            )}
          </FormSection>
          <FormSection>
            {isAppAtNoticeWarningShown && (
              <InfoPanel
                type="warning"
                title="This offering is in the process of being archived"
                message="Keep in mind that any changes will only apply until the subscription end date, unless a new plan is selected."
              />
            )}
          </FormSection>
          <FormSection>
            {isDiscountApprovalFlowEnabled && !!discreteDiscountPromotions.length && !!discretionaryEstimate?.error && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the bill estimation for the following reason"
                    message={discretionaryEstimate?.error?.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {!!cancelApprovalRequestError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={cancelApprovalRequestError.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {hasOpenApprovalRequest && (
              <InfoPanel
                type="warning"
                message={
                  <div className="approval-request-inflight-banner">
                    One or more approval requests are currently in progress. If you perform this action, those approval
                    requests will be canceled automatically.
                    {taskListData?.taskList?.tasks?.length && (
                      <TaskDetailAction
                        task={taskListData.taskList.tasks[0]}
                        text="View approval request details"
                        refetch={refetch}
                        refetchAll={refetch}
                      />
                    )}
                  </div>
                }
              />
            )}
          </FormSection>
          <FormSection>
            {isBillingProfileInValid && (
              <SectionMessage
                appearance="error"
                title={
                  !validateBillToAddress && !currentInvoiceGroup?.defaultPaymentMethod
                    ? 'Update billing account and billing profile'
                    : !validateBillToAddress
                    ? 'Update billing account'
                    : 'Update billing profile'
                }
                testId="invalid-billing-profile"
              >
                <div>
                  <p>Subscription changes can’t be made because there’s:</p>
                  <br />
                  <ul>
                    {!validateBillToAddress ? <li>No bill-to address available for the billing account</li> : null}

                    {!currentInvoiceGroup?.defaultPaymentMethod ? (
                      <li>No payment method available for the billing profile</li>
                    ) : null}
                  </ul>
                  <br />
                  <p>
                    {`Add the information${validateBillToAddress ? ' or switch the billing profile' : ''}`}, then try
                    again. Still having trouble? Contact the <strong>#help-advocate-central</strong> channel.
                  </p>
                </div>
              </SectionMessage>
            )}
          </FormSection>
          <FormSection>
            <InflightInvoiceBanner
              transactionAccountId={entitlement.transactionAccountId as string}
              message={'An invoice is currently being generated. Any changes you make now will appear on this invoice.'}
            />
          </FormSection>
          <div className="progress-content">
            <ProgressTracker items={items} spacing="compact" />
          </div>
          <FormSection>
            {formError && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to create transaction account for the following reason"
                    message={getFormError(formError, OrderItemType.AMENDMENT_ORDER)}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    requestTraceId={formErrorTraceId}
                    serviceName={formErrorSvcName}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
          </FormSection>
          <FormSection>
            {!!approvalErrorForDiscount && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the discount promotion for the following reason"
                    message={approvalErrorForDiscount.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
            {!!approvalErrorForPricing && (
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    title="We're unable to process the pricing plan change for the following reason"
                    message={approvalErrorForPricing.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            )}
          </FormSection>
          {orderPreviewData?.error && currentProgressIndex === FormTrackerStep.REVIEW && (
            <FormSection>
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    message={orderPreviewData?.error?.message}
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            </FormSection>
          )}
          {isPricingEligibilityMismatching && (
            <FormSection>
              <InfoPanel
                type="error"
                message={
                  <ErrorComponents.ErrorMessageOnInfoPanel
                    message="There is a mismatch of price eligibility of billing profile and one of the entitlements. Please correct the price eligibility for either entitlements or billing profile"
                    enableReporting={isEnabled(ENABLE_REPORT_ERROR)}
                    enableTicketCreationOption={enableTicketCreationOption}
                  />
                }
              />
            </FormSection>
          )}
          {!isNil(isFormDirty) && !isFormDirty && currentProgressIndex === FormTrackerStep.PLAN_DETAILS && (
            <FormSection>
              <InfoPanel
                type="information"
                title="No changes detected"
                message={`You have not done any changes to the subscription - ${
                  entitlement.productName || 'Unnamed product'
                } - ${entitlement.entitlementNumber} . Please make changes to proceed furthur.`}
              />
            </FormSection>
          )}
          {currentProgressIndex === FormTrackerStep.PLAN_DETAILS && trialValidationMsg && (
            <FormSection>
              <InfoPanel type="error" title="Changes not allowed" message={trialValidationMsg} />
            </FormSection>
          )}
          {!tierChangeAllowed && currentProgressIndex === FormTrackerStep.PLAN_DETAILS && (
            <FormSection>
              <InfoPanel
                type="error"
                title="There are too many users to make this change"
                message={`This subscription currently has ${tierChangeErrorDetails?.activeUsers} ${getChargeElement(
                  tierChangeErrorDetails?.productId as string | undefined
                )}s. Before a lower user tier can be selected, some users will need to be removed first.`}
              />
            </FormSection>
          )}
          {noActiveOfferingForBillingCycleChange && (
            <FormSection>
              <InfoPanel
                type="error"
                title="Current offering is not active"
                message="Current offering for one of the entitlement is not active. You cannot proceed furthur."
              />
            </FormSection>
          )}
          <FormSection>{renderFormStep()}</FormSection>
          {isApprovalFormRequired() ? (
            <ApprovalFormSection
              approvalType={actualApprovalType}
              approvalRequiredProcesses={approvalRequiredForTypes}
              slackChannel={
                (isApprovalRequiredForPricing ? approvalSlackChannelForPricing : approvalSlackChannelForDiscount) ?? ''
              }
              onCancel={() => closeSidebar()}
              isLoading={
                (approvalProcessingForDiscount && !evaluateDiscountApprovalProcessing) ||
                cancelApprovalRequestProcessing ||
                (approvalProcessingForPricing && !evaluatePricingPlanApprovalProcessing)
              }
              approvalItems={approvalList}
              isDisabled={evaluateDiscountApprovalProcessing || evaluatePricingPlanApprovalProcessing}
            />
          ) : (
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" testId="close-button" onClick={closeSidebar}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isDisabled={isSubmitButtonDisabled()}
                  isLoading={
                    isProcessing ||
                    cancelApprovalRequestProcessing ||
                    isBillingCycleDisabled ||
                    termPaymentValidation.loading
                  }
                  testId="save-button"
                  onClick={trackChangePlanAnalytics}
                >
                  {currentProgressIndex === FormTrackerStep.REVIEW ? 'Submit' : 'Continue'}
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          )}
        </form>
      )}
    </Form>
  );
});
